export interface SectionValidationResult {
  isComplete: boolean
  summary?: string
  displayName?: string
  price?: string
  error?: string
  userPreviews?: Array<{
    userId: string
    name: string | null
    email: string
    username: string | null
    profilePictureUrl: string | null
  }>
}

export interface RitualFormValues {
  title?: string
  type?: string
  dateTime?: string
  repeat?: string
  repeatAt?: string
  repeatAtType?: "DAYS" | "WEEKS" | "MONTHS" | "HOURS" | "DATE"
  duration?: string
  address?: string
  meetingLink?: string
  steps?: string
  participationFee?: "Free" | "Paid" | "Donation-based"
  price?: string
  currency?: string
  feeDetails?: string
  minParticipantRequirement?: boolean
  minParticipants?: string
  registrationDeadline?: string
  description?: string
  whatToBring?: string
  whatYoullExperience?: string
  intention?: string
  websiteOrSocialMedia?: string
  tags?: string[]
  courseModules?: Array<{
    id: string
    title: string
    description: string
    duration: string
    order: number
    isFree: boolean
    videoFile?: File
  }>
}

export interface MediaFiles {
  images: Array<{ id: string; url?: string; isCover: boolean; existingId?: string }>
  videos: Array<{ id: string; url?: string; existingId?: string }>
}

export interface Organizer {
  id: string // user ID or community ID
  type: "user" | "community"
  name: string
  identifier: string | null // username or handle
  avatar?: string
  roleId: string
  roleName: string
  inviteNote?: string
  dataCount?: {
    followers?: number
    members?: number
  }
}

export const validateInfoSection = (values: RitualFormValues): SectionValidationResult => {
  const errors: string[] = []

  if (!values.title || values.title.length < 3) {
    errors.push("Title is required")
  }
  if (!values.type) {
    errors.push("Type is required")
  }
  // DateTime is required for all types except SELF_GUIDED and SELF_GUIDED_COURSE
  const isSelfGuidedType = values.type === "SELF_GUIDED" || values.type === "SELF_GUIDED_COURSE"
  if (!isSelfGuidedType && !values.dateTime) {
    errors.push("Date & time is required")
  }
  // Repeat is required for all types except SELF_GUIDED and SELF_GUIDED_COURSE
  if (!isSelfGuidedType && !values.repeat) {
    errors.push("Repeat is required")
  }
  // Custom repeat fields are required when repeat is "custom"
  if (values.repeat === "custom" && !isSelfGuidedType) {
    if (!values.repeatAt || values.repeatAt.trim() === "") {
      errors.push("Repeat value is required for custom repeat")
    }
    if (!values.repeatAtType) {
      errors.push("Repeat type is required for custom repeat")
    }
  }
  if (!values.duration) {
    errors.push("Duration is required")
  }

  // Address is optional even for IN_PERSON type - removed the required validation
  // as per user requirement to allow rituals without a specific address

  // Meeting link is required for VIRTUAL_COURSE (Virtual Course) type
  if (values.type === "VIRTUAL_COURSE" && !values.meetingLink) {
    errors.push("Meeting link is required for virtual courses")
  }

  if (!values.participationFee) {
    errors.push("Participation fee is required")
  }

  if (values.participationFee === "Paid") {
    if (!values.price) {
      errors.push("Price is required for paid rituals")
    }
    if (!values.feeDetails) {
      errors.push("Fee details are required for paid rituals")
    }
  }

  // Validate minimum participant requirement fields
  if (values.minParticipantRequirement === true) {
    if (!values.minParticipants || values.minParticipants.trim() === "") {
      errors.push("Minimum number of participants is required")
    }
    if (!values.registrationDeadline || values.registrationDeadline.trim() === "") {
      errors.push("Registration deadline is required")
    }
  }

  const isComplete = errors.length === 0
  const summary = isComplete
    ? [
        values.title,
        values.participationFee === "Paid" ? "Paid" : "Free",
        values.type === "IN_PERSON"
          ? "In Person"
          : values.type === "VIRTUAL_COURSE"
            ? "Live Online"
            : values.type === "SELF_GUIDED"
              ? "Self Guided"
              : "Self Guided Course",
        values.duration ? `${values.duration} min` : undefined,
        values.dateTime,
        values.address ? `@ ${values.address}` : undefined,
        // Only show meeting link for VIRTUAL_COURSE type
        values.type === "VIRTUAL_COURSE" && values.meetingLink?.trim()
          ? `Meeting link provided`
          : undefined,
      ]
        .filter(Boolean)
        .join(" • ")
    : undefined

  // Include price for display in accordion (when Paid)
  const price = values.participationFee === "Paid" && values.price ? `$${values.price}` : undefined

  return { isComplete, summary, price, error: errors[0] }
}

export const validateDetailsSection = (values: RitualFormValues): SectionValidationResult => {
  const errors: string[] = []

  if (!values.description || values.description.length < 20) {
    errors.push("Description must be at least 20 characters")
  }

  // Steps are required for SELF_GUIDED type
  if (values.type === "SELF_GUIDED" && !values.steps) {
    errors.push("Steps to perform are required for self-guided rituals")
  }

  if (!values.tags || values.tags.length === 0) {
    errors.push("At least one tag is required")
  }

  const isComplete = errors.length === 0
  const summary = isComplete
    ? values.description?.substring(0, 150) +
      (values.description && values.description.length > 150 ? "..." : "")
    : undefined

  return { isComplete, summary, error: errors[0] }
}

export const validateMediaSection = (mediaFiles: MediaFiles): SectionValidationResult => {
  const errors: string[] = []

  if (!mediaFiles.images || mediaFiles.images.length === 0) {
    errors.push("At least one image is required")
  }

  const hasCover = mediaFiles.images.length > 0 && mediaFiles.images.some(img => img.isCover)

  if (!hasCover) {
    errors.push("Please select a cover image")
  }

  const isComplete = errors.length === 0
  const imageCount = mediaFiles.images.length
  const videoCount = mediaFiles.videos?.length || 0

  const summary = isComplete
    ? imageCount > 0 && videoCount > 0
      ? `${imageCount} photo${imageCount > 1 ? "s" : ""} / ${videoCount} video${videoCount > 1 ? "s" : ""}`
      : imageCount > 0
        ? `${imageCount} photo${imageCount > 1 ? "s" : ""}`
        : `${videoCount} video${videoCount > 1 ? "s" : ""}`
    : undefined

  return { isComplete, summary, error: errors[0] }
}

export interface RitualOrganizerApiItem {
  id: string
  organiserType: "user" | "community"
  organiserId: string
  status: "PENDING" | "JOINED" | "DECLINED"
  inviteNote: string | null
  invitedAt: string | null
  joinedAt: string | null
  user?: {
    id: string
    username: string | null
    name: string | null
    profilePictureUrl: string | null
    coverImage: string | null
  } | null
  community?: {
    id: string
    name: string | null
    handle: string | null
    coverImage: string | null
  } | null
}

export const validateOrganizersSection = (
  organizers: Organizer[],
  apiOrganizers?: RitualOrganizerApiItem[],
): SectionValidationResult => {
  const errors: string[] = []

  // Combine API organizers with locally added organizers
  const allOrganizers: Array<{
    id: string
    name: string
    identifier: string | null
    avatar?: string | null
  }> = []

  // Add API organizers
  if (apiOrganizers) {
    for (const org of apiOrganizers) {
      const isUser = org.organiserType === "user"
      const name = isUser ? org.user?.name || "Unknown" : org.community?.name || "Unknown"
      const identifier: string | null = isUser
        ? (org.user?.username ?? null)
        : (org.community?.handle ?? null)
      const avatar: string | null | undefined = isUser
        ? org.user?.coverImage || org.user?.profilePictureUrl
        : org.community?.coverImage

      allOrganizers.push({
        id: org.id,
        name,
        identifier,
        avatar,
      })
    }
  }

  // Add local organizers that are not in API organizers
  const apiIds = new Set(apiOrganizers?.map(o => o.id) || [])
  for (const org of organizers) {
    if (!apiIds.has(org.id)) {
      allOrganizers.push({
        id: org.id,
        name: org.name,
        identifier: org.identifier,
        avatar: org.avatar,
      })
    }
  }

  if (allOrganizers.length === 0) {
    errors.push("At least one organizer is required")
  }

  const isComplete = errors.length === 0
  const summary = isComplete ? allOrganizers.map(org => org.name).join(", ") : undefined

  const userPreviews = allOrganizers.map(org => ({
    userId: org.id,
    name: org.name,
    email: "",
    username: org.identifier || org.name.split(" ")[0].toLowerCase(),
    profilePictureUrl: org.avatar || null,
  }))

  return { isComplete, summary, displayName: userPreviews[0]?.name || undefined, userPreviews }
}

export const validatePrivacySection = (
  values: RitualFormValues & { privacySettings?: Record<string, string[]> },
): SectionValidationResult => {
  const errors: string[] = []

  const settings = values.privacySettings || {}

  if (!settings.visibility || settings.visibility.length === 0) {
    errors.push("Visibility setting is required")
  }
  if (!settings.comment || settings.comment.length === 0) {
    errors.push("Comment setting is required")
  }
  if (!settings.edit || settings.edit.length === 0) {
    errors.push("Edit setting is required")
  }
  if (!settings.join || settings.join.length === 0) {
    errors.push("Join setting is required")
  }

  const isComplete = errors.length === 0
  const summary = isComplete ? "Privacy" : undefined

  return { isComplete, summary, error: errors[0] }
}
